import { ShowContextProvider, Title, useEditController } from "react-admin";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { HiOutlineUserAdd } from "react-icons/hi";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { IdChip } from "../../components/IdChip";
import { colors, fontFamilyMono } from "../../data/tokens";
import {
  OrphanReasonChip,
  formatOrphanMatricula,
  orphanSituacao,
} from "./orphaned-users-util";

// Rótulo pequeno + valor; não renderiza nada quando o campo está vazio
const InfoItem = ({
  label,
  value,
  mono = false,
  lowercase = false,
}: {
  label: string;
  value?: any;
  mono?: boolean;
  lowercase?: boolean;
}) => {
  if (value === null || value === undefined || String(value).trim() === "")
    return null;
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: colors.textMuted,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13.5,
          color: colors.textStrong,
          overflowWrap: "anywhere",
          ...(mono && { fontFamily: fontFamilyMono }),
          // Minúsculas apenas visuais (e-mails do legado em CAIXA ALTA)
          ...(lowercase && { textTransform: "lowercase" }),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const SectionCaption = ({ text }: { text: string }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: colors.primaryDark,
    }}
  >
    {text}
  </Typography>
);

// Junta só as partes preenchidas (dados legados são esburacados)
const joinParts = (parts: any[], sep = ", ") =>
  parts
    .map((p) => (p === null || p === undefined ? "" : String(p).trim()))
    .filter(Boolean)
    .join(sep);

// Ficha organizada do membro órfão. A versão antiga despejava TODOS os
// campos crus do registro (rótulos em inglês, sem ordem) — incluindo a
// senha legada, que não deve aparecer em tela.
export const OrphanedUsersShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "orphaned-users/crud",
    id,
  });
  const record = controller?.record;

  return (
    <ShowContextProvider value={controller}>
      <Title title="Membros Órfãos" />
      <PageTopBreadcrumb
        title="Membros Órfãos"
        firstPath="/#/orphaned-users/crud"
        secondTitle="Visualizando"
        thirdTitle={record?.sNome}
        actionType="edit"
        actions={
          record ? (
            // Fluxo principal da tela: abrir o cadastro de Sócio já
            // apontando para este órfão (o formulário oferece o
            // auto-preencher com os dados daqui)
            <Button
              variant="outlined"
              startIcon={<HiOutlineUserAdd size={16} />}
              onClick={() => {
                window.location.href = `/#/users/crud/create/${record.id}`;
              }}
            >
              Criar Sócio
            </Button>
          ) : undefined
        }
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.primaryDark,
                }}
              >
                {record.sNome || "(sem nome)"}
              </Typography>
              <OrphanReasonChip reason={record.issueReason} />
            </Box>

            <Divider sx={{ marginY: 2.5 }} />

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.75}>
                  <SectionCaption text="Identificação" />
                  <InfoItem label="CPF" value={record.nCPF} mono />
                  <InfoItem
                    label="Matrícula"
                    value={formatOrphanMatricula(record)}
                    mono
                  />
                  <InfoItem
                    label="Matrícula provisória"
                    value={record.nMatriProv}
                    mono
                  />
                  <InfoItem label="Categoria" value={record.sCatSocio} />
                  <InfoItem
                    label="Situação"
                    value={orphanSituacao(record.sSituacao)}
                  />
                  <InfoItem label="CREA" value={record.sCREA} mono />
                  <InfoItem
                    label="Atividade profissional"
                    value={record.sAtividade}
                  />
                  <InfoItem label="Nascimento" value={record.dDtNasc} />
                  <InfoItem label="Sexo" value={record.sSexo} />
                  <InfoItem label="Estado civil" value={record.sEstadoCivil} />
                  <InfoItem label="Admissão" value={record.dDtAdmis} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.75}>
                  <SectionCaption text="Contato" />
                  <InfoItem
                    label="E-mail"
                    value={record.sEmailPart}
                    lowercase
                  />
                  <InfoItem
                    label="E-mail comercial"
                    value={record.sEmailCome}
                    lowercase
                  />
                  <InfoItem label="Celular" value={record.sCelular} mono />
                  <InfoItem
                    label="Telefone residencial"
                    value={record.sTelResid}
                    mono
                  />
                  <InfoItem
                    label="Telefone comercial"
                    value={record.sTelComer}
                    mono
                  />
                  <InfoItem
                    label="Endereço residencial"
                    value={joinParts(
                      [
                        record.sEndResid,
                        record.sBaiResid,
                        joinParts([record.sCidResid, record.sEstResid], "/"),
                        record.nCepResid && `CEP ${record.nCepResid}`,
                      ],
                      " — "
                    )}
                  />
                  <InfoItem
                    label="Endereço comercial"
                    value={joinParts(
                      [
                        record.sEndComer,
                        record.sBaiComer,
                        joinParts([record.sCidComer, record.sEstComer], "/"),
                        record.sCepComer && `CEP ${record.sCepComer}`,
                      ],
                      " — "
                    )}
                  />
                  <InfoItem
                    label="Formação"
                    value={joinParts(
                      [
                        record.sFormacao,
                        record.sUniversi,
                        record.dConclusao &&
                          `conclusão em ${record.dConclusao}`,
                      ],
                      " — "
                    )}
                  />
                  <InfoItem
                    label="Dependentes"
                    value={joinParts(
                      String(record.sDepende || "").split(","),
                      ", "
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ marginY: 2.5 }} />

            <SectionCaption text="Importação" />
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginTop: 1.25,
                alignItems: "flex-start",
              }}
            >
              <InfoItem label="ID legado" value={record.oldId} mono />
              <InfoItem
                label="Importado em"
                value={
                  record.createdAt
                    ? new Date(record.createdAt).toLocaleDateString("pt-BR")
                    : ""
                }
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.textMuted,
                  }}
                >
                  Identificador técnico
                </Typography>
                <Box sx={{ marginTop: 0.5 }}>
                  <IdChip value={record.id} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
